// Shared supplement grouping + one-line descriptions, used by both the
// /supplements index page and the site nav's Supplements hover menu
// (app/playbook/plan-a/SupplementsList.tsx). Lives in its own module (rather
// than page.tsx) because the nav is a client component and the page pulls in
// fs-reading helpers.

// The supplements are grouped into these sections (in this order), and within
// each section the slugs are listed in reading order. This array is the single
// place to edit to re-title a section, reorder it, or move a supplement between
// sections. Each slug must match a real supplement; any supplement whose slug
// is not listed here still renders — under "Other" at the bottom — so a
// newly-added supplement can never silently disappear.
export const SUPPLEMENT_SECTIONS: { title: string; slugs: string[] }[] = [
  {
    title: "Plan A Basics",
    slugs: [
      "faq",
      "how-plan-a-solves-our-5-biggest-problems",
    ],
  },
  {
    title: "Plan A Policies",
    slugs: [
      "covert-ai-projects",
      "verification-plan",
      "transparency-plan",
      "capability-scaling-strategy",
      "security-in-plan-a",
    ],
  },
  {
    title: "Analysis of Plan A Policies",
    slugs: ["plan-a-assumptions", "comparing-possible-plans", "deal-decline", "takeoff-forecast"],
  },
  {
    title: "Economics",
    slugs: ["economics-of-plan-a", "compute-supplement", "econ-explorer"],
  },
  {
    title: "Speculative Analysis",
    slugs: ["space-governance-plan", "ai-for-epistemics", "alignment-roadmap"],
  },
];

// One-line descriptions shown under each title (index page cards and the nav
// hover preview). Edit these to taste; on the index page any slug without an
// entry falls back to the supplement's extracted summary.
export const SUPPLEMENT_SUBTITLES: Record<string, string> = {
  faq: "Answers to some of the most common objections and questions about Plan A that we have received so far.",
  "how-plan-a-solves-our-5-biggest-problems":
    "How the plan addresses the five biggest risks from AI.",
  "covert-ai-projects":
    "How much do hidden AI projects threaten the deal, and how we can prevent, detect, and deter them?",
  "verification-plan":
    "How can the US and China enforce an AI slowdown without having to trust each other?",
  "transparency-plan":
    "How can relevant information be shared with decision-makers and the public while avoiding downsides?",
  "security-in-plan-a":
    "What security is needed for Plan A in model weights, verification integrity, and algorithmic secrets.",
  "capability-scaling-strategy":
    "The Consortium scales to top-expert AI by 2035, then slows nearly to a pause before handoff.",
  "plan-a-assumptions":
    "Core forecasts and recommendations the scenario rests on, and how confident we are in each.",
  "comparing-possible-plans":
    "Scoring alternative plans on metrics we care about and our odds of them leading to a good future.",
  "deal-decline":
    "How the risk of the deal weakening over time shapes how fast to scale capabilities.",
  "takeoff-forecast":
    "The modified AI Futures Model behind Plan A's trajectories, with an interactive explorer.",
  "economics-of-plan-a":
    "Why we expect full automation and explosive growth from AI and summaries of our economic model.",
  "compute-supplement":
    "Justification of the compute numbers in our scenario.",
  "econ-explorer":
    "An interactive explorer of our economic model.",
  "space-governance-plan":
    "How the resources of space could be allocated in the longer term future.",
  "ai-for-epistemics":
    "How we can use AI to improve human judgment and collective decision-making.",
  "alignment-roadmap":
    "Our best-guess roadmap of the alignment objectives needed to avoid AI takeover.",
};
